import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html, nothing } from "lit";
import { property } from "lit/decorators.js";
import { pdIcons } from "@progressive-development/pd-icon";
import { localized, msg } from "@lit/localize";
import "@progressive-development/pd-forms/pd-button";
//#region src/pd-sidebar/PdSidebar.ts
/**
* @license
* Copyright (c) 2026 PD Progressive Development UG. All rights reserved.
*/
var PdSidebar = class PdSidebar extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.mode = "docked";
		this.open = false;
		this.collapsible = false;
		this.collapsed = false;
		this._handleKeydown = (e) => {
			if (e.key === "Escape" && this.mode === "overlay" && this.open) this._requestClose();
		};
	}
	static {
		this.styles = css`
    :host {
      display: block;
    }

    .panel {
      box-sizing: border-box;
      height: 100%;
      padding: var(--pd-sidebar-padding, 0);
      overflow: hidden;
    }

    .content {
      min-width: 0;
    }

    /* ── docked ─────────────────────────────────────────────────────── */
    :host([mode="docked"]) .panel {
      width: var(--pd-sidebar-width, 17em);
      transition: width var(--pd-sidebar-transition-duration, 0.2s) ease-out;
    }

    :host([mode="docked"][collapsed]) .panel {
      width: var(--pd-sidebar-rail-width, 2.5em);
    }

    :host([mode="docked"][collapsed]) .content {
      display: none;
    }

    .collapse-row {
      display: flex;
      justify-content: flex-end;
      padding-bottom: var(--pd-spacing-sm, 0.5em);
    }

    :host([collapsed]) .collapse-row {
      justify-content: center;
    }

    :host([collapsed]) .collapse-row pd-button {
      transform: scaleX(-1);
    }

    /* ── overlay ────────────────────────────────────────────────────── */
    :host([mode="overlay"]) {
      position: absolute;
      inset: 0;
      z-index: var(--pd-sidebar-z-index, 10);
      pointer-events: none;
    }

    .backdrop {
      position: absolute;
      inset: 0;
      background: var(--pd-sidebar-backdrop-col, rgba(0, 0, 0, 0.3));
      opacity: 0;
      visibility: hidden;
      transition:
        opacity var(--pd-sidebar-transition-duration, 0.2s) ease-out,
        visibility 0s linear var(--pd-sidebar-transition-duration, 0.2s);
    }

    :host([open]) .backdrop {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
      transition: opacity var(--pd-sidebar-transition-duration, 0.2s) ease-out;
    }

    :host([mode="overlay"]) .panel {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: var(--pd-sidebar-width, 17em);
      background: var(--pd-sidebar-bg-col, var(--pd-default-bg-col, #fff));
      box-shadow: var(--pd-shadow-lg);
      overflow-y: auto;
      transform: translateX(calc(-100% - 2em));
      visibility: hidden;
      transition:
        transform var(--pd-sidebar-transition-duration, 0.2s) ease-out,
        visibility 0s linear var(--pd-sidebar-transition-duration, 0.2s);
    }

    :host([mode="overlay"][open]) .panel {
      transform: none;
      visibility: visible;
      pointer-events: auto;
      transition: transform var(--pd-sidebar-transition-duration, 0.2s) ease-out;
    }
  `;
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		document.removeEventListener("keydown", this._handleKeydown);
	}
	updated(changedProperties) {
		if (changedProperties.has("mode") || changedProperties.has("open")) {
			if (this.mode === "overlay" && this.open) document.addEventListener("keydown", this._handleKeydown);
			else document.removeEventListener("keydown", this._handleKeydown);
		}
	}
	render() {
		return html`
      ${this.mode === "overlay" ? html`<div class="backdrop" @click="${this._requestClose}"></div>` : nothing}
      <aside class="panel">
        ${this.collapsible && this.mode === "docked" ? html`
                <div class="collapse-row">
                  <pd-button
                    text=""
                    icon="${pdIcons.ICON_TOGGLE_COLLAPSE}"
                    icon-label="${this.collapsed ? msg("Seitenleiste ausklappen", { id: "pd.sidebar.expand" }) : msg("Seitenleiste einklappen", { id: "pd.sidebar.collapse" })}"
                    @click="${this._toggleCollapsed}"
                  ></pd-button>
                </div>
              ` : nothing}
        <div class="content"><slot></slot></div>
      </aside>
    `;
	}
	_requestClose() {
		this.open = false;
		this.dispatchEvent(new CustomEvent("sidebar-open-change", {
			detail: { open: false },
			bubbles: true,
			composed: true
		}));
	}
	_toggleCollapsed() {
		this.collapsed = !this.collapsed;
		this.dispatchEvent(new CustomEvent("sidebar-collapse-change", {
			detail: { collapsed: this.collapsed },
			bubbles: true,
			composed: true
		}));
	}
};
__decorate([property({
	type: String,
	reflect: true
})], PdSidebar.prototype, "mode", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdSidebar.prototype, "open", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdSidebar.prototype, "collapsible", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdSidebar.prototype, "collapsed", void 0);
PdSidebar = __decorate([localized()], PdSidebar);
//#endregion
export { PdSidebar };
